import 'vue3-toastify/dist/index.css';
import './assets/main.css';

import { createApp } from 'vue';
import { createPinia } from 'pinia';
import { createSentryPiniaPlugin } from '@sentry/vue';
import Vue3Toastify from 'vue3-toastify';
import { plugin, defaultConfig } from '@formkit/vue';
import { VueQueryPlugin } from '@tanstack/vue-query';
import CircleFlags from 'vue-circle-flags';

import App from './App.vue';
import * as Sentry from '@sentry/vue';
import router from './router';
import formkitConfig from '../formkit.config';
import { QueryStaleTime } from '@/constants/http';
import GlobalIcons from '@/plugins/global-icons';
import GlobalComponents from '@/plugins/global-components';
import { useAppConfigStore } from '@/stores/appConfig';
import { useAuthStore } from '@/stores/auth';
import { initAuth0 } from '@/plugins/auth0';
import { checkAuthAndPermission, setSentryUserFromAuthStore } from '@/utils/auth';

// Set dayjs locale
import dayjs from 'dayjs';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import { importDayjsLocale } from '@/utils/dates';

dayjs.extend(localizedFormat);
importDayjsLocale().then((locale) => {
  dayjs.locale(locale);
});

const app = createApp(App);
const pinia = createPinia();

app.use(pinia);
app.use(plugin, defaultConfig(formkitConfig));
app.use(VueQueryPlugin, {
  queryClientConfig: {
    defaultOptions: {
      queries: {
        staleTime: QueryStaleTime,
      },
    },
  },
  enableDevtoolsV6Plugin: true,
});
app.use(Vue3Toastify, {
  autoClose: 4000,
  closeButton: false,
  hideProgressBar: true,
  closeOnClick: false,
  expandCustomProps: true,
  useHandler: (instance) => {
    instance.use(GlobalComponents);
    instance.use(GlobalIcons);
  },
});
app.use(CircleFlags);
app.use(GlobalIcons);
app.use(GlobalComponents);

(async () => {
  const appConfig = useAppConfigStore();
  const authStore = useAuthStore();

  await appConfig.getConfig();
  const auth0 = initAuth0(appConfig.vars);

  if (appConfig.vars.ENABLE_SENTRY) {
    const replaysSessionSampleRate = appConfig.isProduction ? 1.0 : 0.1;
    Sentry.init({
      app,
      dsn: 'https://e1d3d0f8ffc9d70b5cd95854696ca7e7@o4504752826810368.ingest.us.sentry.io/4509204216872960',

      // Adds request headers and IP for users, for more info visit:
      // https://docs.sentry.io/platforms/javascript/guides/vue/configuration/options/#sendDefaultPii
      sendDefaultPii: true,

      environment: appConfig.vars.ENVIRONMENT,
      release: appConfig.vars.RELEASE,
      tracePropagationTargets: ['localhost'],

      integrations: [
        Sentry.browserTracingIntegration({ router }),
        Sentry.replayIntegration({
          unmask: ['[data-sentry-unmask]'],
          unblock: ['[data-sentry-unblock]'],
        }),
      ],

      // Capture Replay for 10% of all sessions,
      // plus for 100% of sessions with an error
      // https://docs.sentry.io/platforms/javascript/session-replay/configuration/#general-integration-configuration
      replaysSessionSampleRate,
      replaysOnErrorSampleRate: 1.0,
    });

    // enable pinia state for sentry
    pinia.use(createSentryPiniaPlugin());

    // set user to easily find replay sessions
    setSentryUserFromAuthStore(auth0);
  }

  router.beforeEach(checkAuthAndPermission(auth0, authStore));

  app.use(router);
  app.use(auth0);

  if (import.meta.env.VITE_ENABLE_MOCKING === 'true') {
    async function enableMocking() {
      const { worker } = await import('./mocks/browser');
      return worker.start({ onUnhandledRequest: 'bypass' });
    }

    await enableMocking();
  }

  app.mount('#app');
})();
